import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { ReconciledBadge } from '@/components/accounting/ReconciledBadge';
import { VarianceDialog } from '@/components/accounting/VarianceDialog';

// iter951 — Thin network-wide reconciliation strip at the very top of the
// Dashboard. Live-checks Grand Total Debits − Grand Total Credits from the
// tenant-wide Trial Balance every 60s. Reuses ReconciledBadge.
export default function DashboardHealthStrip() {
  const navigate = useNavigate();
  const [tb, setTb] = useState(null);
  const [varianceOpen, setVarianceOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get('/accounting/trial-balance');
      setTb(r.data || null);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  if (!tb) return null;
  const balanced = tb.is_balanced ?? Math.abs((tb.total_debits || 0) - (tb.total_credits || 0)) < 0.01;
  return (
    <>
      <button
        type="button"
        onClick={() => (balanced ? navigate('/trial-balance') : setVarianceOpen(true))}
        data-testid="dashboard-health-strip"
        className={`w-full flex items-center gap-3 rounded-lg border px-3.5 py-2 text-left transition-colors ${
          balanced
            ? 'bg-emerald-50/70 border-emerald-200 hover:bg-emerald-100/70'
            : 'bg-red-50/70 border-red-200 hover:bg-red-100/70'
        }`}
      >
        <ReconciledBadge debit={tb.total_debits} credit={tb.total_credits} difference={tb.difference} />
        <span className={`text-[12px] font-medium ${balanced ? 'text-emerald-800' : 'text-red-800'}`}>
          {balanced
            ? 'Books are balanced network-wide — debits equal credits.'
            : `Ledger variance detected: ${(Math.round(((tb.total_debits || 0) - (tb.total_credits || 0)) * 100) / 100).toLocaleString()}. Click to view the entries.`}
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold opacity-70">
          {balanced ? 'Trial Balance' : 'View variance'} <ChevronRight size={13} />
        </span>
      </button>
      <VarianceDialog open={varianceOpen} onOpenChange={setVarianceOpen} />
    </>
  );
}
